import PropTypes from "prop-types";
import React, { Fragment, useCallback, useEffect, useMemo, useState, useContext, useRef } from "react";
import { HiOutlineTrash, HiOutlineSwatch, HiOutlineEllipsisVertical, HiOutlineClipboardDocument, HiCheck } from "react-icons/hi2";
import { connect } from "react-redux";
import { useReactFlow } from "reactflow";

import { Menu, Transition } from "@headlessui/react";
import ConfirmDialog from "../../../components/confirm-dialog/confirm-dialog";
import Message from "../../../components/toast-message";
import { classNames } from "../../../helpers/utils/css-utils";
import { stmSettingSave } from "../../../redux/journey-toolbox/journey-toolbox-actions";
import journeyStmStore from "../common/service/journey-stm-store";
import { settingsTypes } from "./settings-types-registry";
import { WorkflowCanvasContext } from "../../../contexts/WorkflowCanvasContextProvider";
import IntentPrompt from "../common/components/intent-prompt";

const ChildNode = React.memo((props) => {
  // Use a ref to keep track of the latest intentPrompt to inject it when child calls onChangeData
  const intentPromptRef = useRef(props.savedSettings?.intentPrompt || "");

  useEffect(() => {
    if (props.savedSettings?.intentPrompt !== undefined) {
      intentPromptRef.current = props.savedSettings.intentPrompt;
    }
  }, [props.savedSettings]);

  const handleChildChangeData = useCallback((newData) => {
    props.onChangeData({
      ...newData,
      intentPrompt: intentPromptRef.current
    });
  }, [props.onChangeData]);

  const handleIntentChange = useCallback((e) => {
    const value = e.target.value;
    intentPromptRef.current = value;
    props.onChangeData({
      ...props.savedSettings,
      intentPrompt: value
    });
  }, [props.onChangeData, props.savedSettings]);

  const childrenWithProps = React.Children.map(props.children, (child) => {
    const childProps = {
      id: props.id,
      name: props.name,
      onChangeData: handleChildChangeData,
      savedSettings: props.savedSettings,
      metadata: props.metadata,
      closeSettingPage: props.closeSettingPage,
    };

    if (React.isValidElement(child)) {
      return React.cloneElement(child, childProps);
    }

    return child;
  });

  return (
    <div className="flex flex-col gap-4 pb-4">
      <div>{childrenWithProps}</div>
      <div className="pt-4">
        <IntentPrompt
          value={props.savedSettings?.intentPrompt || ""}
          onChange={handleIntentChange}
        />
      </div>
    </div>
  );
});

function Settings({ closeSettingPage }) {
  const workflowCanvasContext = useContext(WorkflowCanvasContext);
  const nodeAttribute = workflowCanvasContext.nodeAttribute
  const metadata = workflowCanvasContext.metadata
  const { id, nodeType, label: name } = nodeAttribute

  const [dialogSettings, setDialogSettings] = useState({
    open: false,
    message: null,
  });
  const { deleteElements } = useReactFlow();

  const [settingMenuExpand, setSettingMenuExpand] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  // Predefined color options
  const colorOptions = [
    { name: 'White', value: null },
    { name: 'Red Light', value: '#FCA5A5' },
    { name: 'Orange Light', value: '#FCD59F' },
    { name: 'Yellow Light', value: '#FDE68A' },
    { name: 'Green Light', value: '#6EE7B7' },
    { name: 'Teal Light', value: '#99F6E4' },
    { name: 'Blue Light', value: '#BFDBFE' },
    { name: 'Indigo Light', value: '#C7D2FE' },
    { name: 'Purple Light', value: '#DDD6FE' },
    { name: 'Gray Light', value: '#D1D5DB' },
    { name: 'Pink Light', value: '#FBCFE8' },
    { name: 'Rose Light', value: '#FECACA' },
    { name: 'Amber Light', value: '#FCD34D' },
    { name: 'Lime Light', value: '#D9F99D' },
    { name: 'Emerald Light', value: '#A7F3D0' },
    { name: 'Cyan Light', value: '#A5F3FC' },
    { name: 'Sky Blue Light', value: '#BAE6FD' },
    { name: 'Violet Light', value: '#E9D5FF' },
    { name: 'Slate Light', value: '#E2E8F0' },
    { name: 'Zinc Light', value: '#E4E4E7' }
  ];

  // Get the current color from stateConfig or use default
  const currentColor = nodeAttribute.stateConfig?.color || colorOptions[0].value;

  let content;
  let title = "";

  const settingsType = settingsTypes[nodeType];

  if (settingsType) {
    title = settingsType.title;
    content = settingsType.component;
  }

  const onChangeData = useCallback((data) => {
    workflowCanvasContext.handleNodeFullConfigChange(data)
  }, []);

  const onClickDelete = (nodeTitle) => {
    setDialogSettings({
      open: true,
      message: `Are you sure you want to delete "${nodeTitle}"`,
    })
  }

  const onDeleteConfirm = (yes) => {
    if (yes) {
      workflowCanvasContext.handleDelete(nodeAttribute.parentId, nodeAttribute.id)
    }
    setDialogSettings((preSettings) => ({
      ...preSettings,
      open: false,
    }));
    closeSettingPage();
  }

  const handleColorSelect = (colorValue) => {
    // Update the stateConfig with the selected color
    const updatedConfig = {
      ...nodeAttribute.stateConfig,
      color: colorValue
    };

    // Call onChangeData to update the state
    onChangeData(updatedConfig);

    // Close the color picker
    setColorPickerOpen(false);
  };

  const [copySuccess, setCopySuccess] = useState();

  const copyToClipboard = async e => {
    if ('clipboard' in navigator) {
      await navigator.clipboard.writeText(id);
    } else {
      document.execCommand('copy', true, id);
    }
    setCopySuccess(true);
  };

  useEffect(() => {
    if (copySuccess) {
      setTimeout(() => {
        setCopySuccess(false);
      }, 2000)
    }
  }, [copySuccess]);


  const memoizedMetadata = useMemo(() => metadata, [metadata.journeyId, metadata.slots]);

  const options = [
    {
      name: 'Copy Step Id',
      onClick: () => { copyToClipboard() }
    }
  ]

  return (
    <>
      <div className="p-0 box-border scrollbars h-[calc(100vh-4rem)] overflow-hidden overflow-y-auto">
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/90 px-5 py-4 backdrop-blur-md">
          <div className="flex items-start justify-between gap-3 pr-10">
            <div className="min-w-0">
              <p className="text-[11px] font-medium uppercase tracking-wider text-gray-400">
                Step settings
              </p>
              <p className="truncate text-lg font-semibold text-gray-900">
                {title}
              </p>
            </div>

            <div className="flex shrink-0 items-center gap-1">
              {/* Color Picker */}
              <div className="relative">
                <button
                  type="button"
                  title="Step colour"
                  aria-label="Step colour"
                  onClick={() => setColorPickerOpen(!colorPickerOpen)}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors duration-200
                    hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <span className="relative">
                    <HiOutlineSwatch className="h-5 w-5" />
                    <span
                      className="absolute -bottom-1 -right-1 h-2.5 w-2.5 rounded-full border border-white shadow-sm"
                      style={{ backgroundColor: currentColor || "#ffffff" }}
                    />
                  </span>
                </button>

                {colorPickerOpen && (
                  <div className="absolute right-0 z-30 mt-2 w-56 rounded-xl border border-gray-200 bg-white p-3 shadow-lg shadow-gray-200/60 dark:shadow-black/40">
                    <p className="mb-2 text-xs font-medium text-gray-500">
                      Step colour
                    </p>
                    <div className="grid grid-cols-5 gap-2">
                      {colorOptions.map((color) => (
                        <button
                          key={color.name}
                          onClick={() => handleColorSelect(color.value)}
                          title={color.name}
                          className={classNames(
                            "flex h-8 w-8 items-center justify-center rounded-lg border transition-transform duration-150 hover:scale-110",
                            currentColor === color.value
                              ? "border-primary ring-2 ring-primary/30 dark:border-brand-lime dark:ring-brand-lime/30"
                              : "border-gray-200"
                          )}
                          style={{ backgroundColor: color.value || "#ffffff" }}
                        >
                          {currentColor === color.value && (
                            <HiCheck className="h-4 w-4 text-gray-900" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Delete */}
              <button
                type="button"
                title="Delete step"
                aria-label="Delete step"
                onClick={() => onClickDelete(title)}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors duration-200
                  hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500
                   dark:hover:bg-red-500/10 dark:hover:text-red-400"
              >
                <HiOutlineTrash className="h-5 w-5" />
              </button>

              {/* Overflow menu */}
              <Menu as="div" className="relative">
                <Menu.Button
                  title="More options"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-500 transition-colors duration-200
                    hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <span className="sr-only">Open options</span>
                  <HiOutlineEllipsisVertical className="h-5 w-5" />
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-150"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-100"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-xl border border-gray-200 bg-white p-1.5 shadow-lg shadow-gray-200/60 focus:outline-none dark:shadow-black/40">
                    {options.map((option, oi) => (
                      <Menu.Item key={oi}>
                        {({ active }) => (
                          <button
                            type="button"
                            className={classNames(
                              active
                                ? "bg-gray-100 text-gray-900"
                                : "text-gray-700",
                              "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors duration-150"
                            )}
                            onClick={() => option.onClick()}
                          >
                            <HiOutlineClipboardDocument className="h-4 w-4 shrink-0" />
                            {option.name}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>
        {/* Setting options */}
        {content ? (
          <div className="block px-5 py-5">
            <ChildNode
              key={id}
              id={id}
              name={name}
              metadata={memoizedMetadata}
              savedSettings={nodeAttribute.stateConfig}
              onChangeData={onChangeData}
              closeSettingPage={closeSettingPage}
            >
              {content}
            </ChildNode>
          </div>
        ) : (
          <div className="flex h-[70vh] flex-1 flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
              <HiOutlineSwatch className="h-6 w-6 text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-600">
              No settings for {title}
            </p>
            <p className="max-w-[220px] text-xs text-gray-400">
              This step runs as-is and has nothing to configure.
            </p>
          </div>
        )}

        <div className="flex flex-row justify-center items-center">
          {copySuccess &&
            <Message text={`Copied ${id}`} type="success" />
          }
        </div>
      </div>
      <ConfirmDialog
        onCompletion={onDeleteConfirm}
        {...dialogSettings}
      />
    </>
  );
}

const mapsStateToProps = (state, ownProps) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    stmSettingSaveHandle: (data) => {
      dispatch(stmSettingSave(data));
    },
  };
};

Settings.propTypes = {
  selectedNode: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    nodeIndex: PropTypes.number,
    type: PropTypes.string,
  }),
  metadata: PropTypes.shape({
    journeyId: PropTypes.string,
    slots: PropTypes.object,
  }),
};

export default connect(mapsStateToProps, mapDispatchToProps)(Settings);
